<template>
  <div id="production-table">
    <div class="table-header">
      <v-switch
        color="primary"
        label="Toolpath Mode"
        @update:model-value="
          () => {
            toolpathMode = !toolpathMode
          }
        "
        hide-details
        style="margin-right: auto"
      ></v-switch>
      <v-chip
        v-if="['sho@plykea.com', 'kath@plykea.com', 'adam@plykea.com'].includes($store.state.user.email)"
        class="text-body-2 mr-4"
        color="green"
        >{{ ukOrderTally }}</v-chip
      >
      <v-text-field
        v-model="tableSearch"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        :clearable="true"
      ></v-text-field>
    </div>
    <v-data-table
      :items="toolpathMode ? shipmentsForToolpath : shipments"
      :headers="toolpathMode ? toolpathModeHeaders : headers"
      :search="tableSearch"
      :custom-filter="customSearch"
      :loading="loading"
      must-sort
      :sort-by="activeSortBy"
      @update:sort-by="onSortByChange"
    >
      <template v-slot:item="{ item }">
        <tr
          :class="{
            paused: item.paused
          }"
        >
          <td>
            <v-icon v-if="item.balanceOutstanding" size="small" class="mr-2" color="warning">mdi-truck</v-icon>
            {{ item.orderNumber || item.invoiceNumber }}
          </td>
          <td :class="{ 'text-truncate': !tableSearch || tableSearch.length < 2 }">
            {{
              item.quoteEmail && item.quoteEmail !== item.customerEmail
                ? item.customerEmail + ' / ' + item.quoteEmail
                : item.customerEmail
            }}
          </td>
          <td>
            <div class="d-flex justify-space-between">
              <div>
                {{ formatLineItems(item.lineItems) }}
              </div>
              <div>
                <v-tooltip location="top" :open-delay="500">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      size="small"
                      style="line-height: 14px; margin: 0 0 0 auto"
                      @click="editNotes(item)"
                      v-bind="props"
                      >mdi-comment</v-icon
                    >
                  </template>
                  <span>Order Notes</span>
                </v-tooltip>
              </div>
            </div>
          </td>
          <td>
            <DatePickerPlus
              :timestamp="item.materialOrdered"
              :clearable="true"
              placeholder="Material not ordered"
              v-on:update:timestamp="
                (val) => {
                  item.materialOrdered = val
                  changed(item, 'materialOrdered')
                }
              "
            ></DatePickerPlus>
          </td>
          <td>
            <DatePickerPlus
              :timestamp="item.toolpathed"
              :clearable="true"
              :placeholder="'Requires toolpathing'"
              v-on:update:timestamp="
                (val) => {
                  item.toolpathed = val
                  changed(item, 'toolpathed')
                }
              "
            ></DatePickerPlus>
          </td>
          <td>
            <div v-if="item.paymentDate" style="display: flex; align-items: center; justify-content: space-between">
              <div>
                {{
                  toolpathMode
                    ? `${calcDate('payment', item.paymentDate)} days (${$utils
                        .momentFromTimestamp(item.paymentDate)
                        .format('Do MMM')})`
                    : $utils.momentFromTimestamp(item.paymentDate).format('Do MMM')
                }}
              </div>
            </div>
          </td>
          <td v-if="!toolpathMode">
            <div v-if="item.requiresSprayer !== false">
              <div v-if="item.sprayerDate" style="display: flex; align-items: center; justify-content: space-between">
                <div>
                  {{ $utils.momentFromTimestamp(item.sprayerDate).format('Do MMM') }}
                </div>
              </div>
            </div>
          </td>
          <td>
            <div style="display: flex; align-items: center; justify-content: space-between">
              <DatePickerPlus
                v-if="!toolpathMode"
                :timestamp="item.deliveryDate"
                :clearable="true"
                placeholder="Target Delivery"
                v-on:update:timestamp="
                  (val) => {
                    item.deliveryDate = val
                    changed(item, 'deliveryDate')
                  }
                "
              ></DatePickerPlus>
              <div v-if="item.deliveryDate && toolpathMode">
                {{
                  calcDate('deadline', item.deliveryDate) +
                  ' days (' +
                  $utils.momentFromTimestamp(item.deliveryDate).format('Do MMM') +
                  ')'
                }}
              </div>
              <v-icon size="small" class="ml-2" v-if="item.userSpecifiedDeliveryDate">mdi-account</v-icon>
            </div>
          </td>
          <td>
            <div v-if="item.dateScheduled" style="display: flex; align-items: center; justify-content: space-between">
              <div>
                {{ $utils.momentFromTimestamp(item.dateScheduled).format('Do MMM') }}
              </div>
            </div>
            <div v-else>Not scheduled</div>
          </td>
          <td class="text-right">
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-2" @click="viewQuote(item)" v-bind="props">mdi-clipboard-text</v-icon>
              </template>
              <span>Original Quote</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-2" @click="materialView(item)" v-bind="props">mdi-cart</v-icon>
              </template>
              <span>Material Order List</span>
            </v-tooltip>
            <!-- Pause / Play -->
            <v-tooltip v-if="item.paused" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon
                  size="small"
                  class="mr-2"
                  @click="
                    () => {
                      pauseShipmentDialog = true
                      selectedShipment = item
                    }
                  "
                  v-bind="props"
                  >mdi-play-circle</v-icon
                >
              </template>
              <span>Unpause Shipment</span>
            </v-tooltip>
            <v-tooltip v-else location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon
                  size="small"
                  class="mr-2"
                  @click="
                    () => {
                      pauseShipmentDialog = true
                      selectedShipment = item
                      findQuoteFromShipment()
                    }
                  "
                  v-bind="props"
                  >mdi-pause-circle</v-icon
                >
              </template>
              <span>Pause Shipment</span>
            </v-tooltip>
            <v-tooltip v-if="!toolpathMode" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-2" @click="schedule(item)" v-bind="props">mdi-calendar-range</v-icon>
              </template>
              <span>Schedule</span>
            </v-tooltip>
            <v-tooltip v-if="!toolpathMode" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-2" @click="sprayerView(item)" v-bind="props">mdi-spray</v-icon>
              </template>
              <span>Sprayer List</span>
            </v-tooltip>
            <v-tooltip v-if="!toolpathMode" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-2" @click="packingView(item)" v-bind="props">mdi-clipboard-check</v-icon>
              </template>
              <span>Packing List</span>
            </v-tooltip>
            <v-tooltip v-if="!toolpathMode" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-2" @click="printableSVG(item)" v-bind="props">mdi-chart-tree</v-icon>
              </template>
              <span>Visual</span>
            </v-tooltip>
            <v-tooltip v-if="$store.state.user.permissions.admin && !toolpathMode" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon size="small" class="mr-2" @click="openDispatchDialog(item)" v-bind="props">mdi-truck</v-icon>
              </template>
              <span>Mark as dispatched</span>
            </v-tooltip>
            <v-tooltip v-if="!toolpathMode" location="top" :open-delay="500">
              <template v-slot:activator="{ props }">
                <v-icon
                  :disabled="
                    !['sho@plykea.com', 'kath@plykea.com', 'adam@plykea.com'].includes($store.state.user.email)
                  "
                  size="small"
                  @click="
                    () => {
                      deleteShipmentDialog = true
                      selectedShipment = item
                    }
                  "
                  v-bind="props"
                  >mdi-delete</v-icon
                >
              </template>
              <span>Delete Shipment</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Component Overlay Dialog -->
    <v-dialog v-model="dialogVisible" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="primary" class="no-print elevation-0">
          <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="
              () => {
                dialogVisible = false
                overlayComponent = null
              }
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <component
          :is="overlayComponent"
          :shipment="selectedShipment"
          :quoteID="selectedShipment.quoteID"
          :versionID="selectedShipment.versionID"
        ></component>
        <v-card-text> </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Pause -->
    <v-dialog v-model="pauseShipmentDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">
          {{ selectedShipment && selectedShipment.paused ? 'Unpause Shipment' : 'Pause Shipment' }}
        </v-card-title>
        <v-card-text v-if="selectedShipment && !selectedShipment.paused" class="pt-6">
          <div class="text-h6">Why?</div>
          <v-form ref="pauseForm">
            <v-textarea
              v-model="pauseForm.why"
              :rules="[(v) => !!v || 'A reason is required']"
              variant="outlined"
              placeholder="Please leave a brief note to explain why the order is being paused"
            ></v-textarea>
            <v-checkbox class="text-h6" v-model="pauseForm.changesCheck" label="Changes Have Been Made"></v-checkbox>
            <p v-if="pauseForm.changesCheck" class="text-caption text-red">
              Please state changes concisely.<br />
              Production notes will be generated from the latest pause; gather a full list of changes made.<br />
              Edits to the change statement can be made.
            </p>
            <div class="text-h6" v-if="pauseForm.changesCheck">Changes:</div>
            <v-textarea
              v-if="pauseForm.changesCheck"
              v-model="pauseForm.changes"
              :rules="[(v) => !!(v && v.trim()) || 'Describe the changes — this becomes the production notes']"
              variant="outlined"
              placeholder="List the changes in the format: [material] - [unit numbers] - [state changes]"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card
          v-else
          id="tasks-dialog"
          @input="
            () => {
              !pauseShipmentDialog
              selectedShipment = {}
            }
          "
        >
          <v-card-title class="bg-primary text-white text-h6"> Pause Log </v-card-title>
          <v-card-text>
            <div v-for="(item, key) of selectedShipment.pauseLog" :key="key" class="pause-log-item mt-4">
              <div class="text-caption d-flex">
                <strong>
                  {{ `${item.author} - ${$moment.unix(item.date.seconds).format('Do MMM YYYY')}` }}
                </strong>
                <v-spacer></v-spacer>
                <v-icon
                  class="mr-1"
                  @click="
                    () => {
                      editPauseLogDialog = true
                      selectedPauseLog = item
                      selectedPauseLogKey = key
                      findQuoteFromShipment()
                    }
                  "
                  >mdi-pencil</v-icon
                >
                <v-icon
                  class="mr-1"
                  color="red"
                  @click="
                    () => {
                      deletePauseLogDialog = true
                      selectedPauseLog = item
                      selectedPauseLogKey = key
                      findQuoteFromShipment()
                    }
                  "
                  >mdi-delete
                </v-icon>
              </div>
              <div class="text-caption pt-2">Why?</div>
              <div class="text-body-1">{{ item.why }}</div>
              <div class="text-caption pt-2">Changes?</div>
              <div class="text-body-1">{{ item.changes || 'None' }}</div>
            </div>
          </v-card-text>
        </v-card>
        <div class="d-flex flex-column justify-end">
          <v-alert v-if="selectedShipment && selectedShipment.paused" type="warning" class="d-block">
            All work in production will restart if you unpause this shipment! <br />
            Please check that the latest change statement is accurate as this will be generated as production notes.
          </v-alert>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="pauseShipmentDialog = false">Cancel</v-btn>
            <v-btn
              color="warning"
              variant="flat"
              :disabled="pauseBlocked"
              @click="toggleShipmentPause(selectedShipment)"
              >{{ selectedShipment && selectedShipment.paused ? 'Unpause' : 'Pause' }}</v-btn
            >
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>

    <!-- EDIT PAUSE LOG -->
    <v-dialog v-model="editPauseLogDialog" width="500" @update:model-value="updatePauseLog(selectedPauseLogKey)">
      <v-card>
        <v-card-title class="bg-primary text-h6 text-white">Edit Pause Log</v-card-title>
        <v-card-text class="mt-6">
          <div class="text-caption pt-2">Why</div>
          <div class="text-body-1 mb-2">{{ selectedPauseLog.why }}</div>
          <div class="text-caption pt-2">Edit Change Statement</div>
          <v-textarea v-model="selectedPauseLog.changes"></v-textarea>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- DELETE PAUSE LOG -->
    <v-dialog v-model="deletePauseLogDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-h6 text-white">Delete Pause Log</v-card-title>
        <v-card-text class="mt-6">
          <div class="text-caption pt-2">Why</div>
          <div class="text-body-1 mb-2">{{ selectedPauseLog.why }}</div>
          <div class="text-caption pt-2">Edit Change Statement</div>
          <div class="text-body-1 mb-2">{{ selectedPauseLog.changes }}</div>
        </v-card-text>
        <v-alert type="warning" class="pt-6">
          Are you sure you want to delete this pause log? <br />
          Remember the previous pause log will then be used for production notes.
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deletePauseLogDialog = false">Cancel</v-btn>
          <v-btn
            color="warning"
            variant="flat"
            @click="deletePauseLog(selectedPauseLogKey)"
            :disabled="deletingPauseLog"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteShipmentDialog" width="500">
      <v-card>
        <v-card-title class="bg-warning text-white text-h6">Delete Shipment</v-card-title>
        <v-card-text class="pt-6">
          Are you sure you want to delete this shipment and all associated tasks?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deleteShipmentDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="deleteShipment(selectedShipment)">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dispatchedDialog" width="540" persistent>
      <v-card>
        <v-card-title class="bg-warning text-white text-h6">Dispatch Shipment</v-card-title>
        <v-card-text class="pt-6">
          <div v-if="!selectedOrder" class="text-grey">Loading order…</div>
          <template v-else>
            <p class="mb-2">
              Order <strong>{{ selectedOrder.orderNumber }}</strong>
              <span v-if="selectedOrder.region" class="ml-1 text-caption text-uppercase text-grey"
                >({{ selectedOrder.region }})</span
              >
            </p>

            <p v-if="selectedOrder.balanceOutstanding" class="text-warning mb-2">
              Balance still outstanding — invoice cannot be raised until paid.
            </p>

            <p
              v-if="selectedOrder.region === 'eu' && (selectedOrder.dispatchCounter || 0) > 0 && isAdmin"
              class="text-error mb-2"
            >
              Already dispatched.
              <span v-if="selectedOrder.xeroInvoiceNumber">
                Existing Xero invoice: <strong>{{ selectedOrder.xeroInvoiceNumber }}</strong>
                — void it manually before retrying.
              </span>
            </p>

            <p v-if="dispatchError" class="text-error mb-2">{{ dispatchError }}</p>

            <p v-if="selectedOrder.region === 'eu' && dispatchedInvoiceURL" class="text-success mb-2">
              Invoice raised:
              <a :href="dispatchedInvoiceURL" target="_blank" rel="noopener">{{ dispatchedInvoiceURL }}</a>
            </p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="selectedOrder && selectedOrder.region === 'eu' && (selectedOrder.dispatchCounter || 0) > 0 && isAdmin"
            color="error"
            variant="text"
            :disabled="dispatching"
            @click="resetDispatchCounter"
            >Reset Counter</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" :disabled="dispatching" @click="dispatchedDialog = false">Close</v-btn>
          <v-btn
            color="warning"
            variant="flat"
            :loading="dispatching"
            :disabled="
              !selectedOrder ||
              selectedOrder.balanceOutstanding ||
              dispatchCompleted ||
              (selectedOrder.region === 'eu' &&
                (((selectedOrder.dispatchCounter || 0) > 0 && !isAdmin) || !!dispatchedInvoiceURL))
            "
            @click="confirmDispatch"
            >{{
              selectedOrder && selectedOrder.region === 'eu' && (selectedOrder.dispatchCounter || 0) > 0
                ? 'Retry'
                : 'Confirm Dispatch'
            }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Schedule Dialog -->
    <v-dialog v-model="scheduleDialog" width="500" @update:model-value="scheduleDialogClose">
      <v-card id="tasks-dialog">
        <v-card-title class="bg-primary text-white text-h6"> Task Schedule </v-card-title>
        <v-progress-linear color="accent" :indeterminate="true" v-if="taskSchedule == null"></v-progress-linear>
        <v-card-text class="pt-4">
          <div class="tasks">
            <div
              v-for="task in taskSchedule"
              :class="[task.data().type, { coloured: !task.data().complete }]"
              class="task d-flex justify-space-between pa-2 my-2 text-capitalize"
              :key="task.id"
            >
              <div>
                {{ task.data().type }} {{ task.data().objectType }}
                {{
                  task.data().type == 'dispatch' ? (task.data().subtype ? task.data().subtype : task.data().type) : ''
                }}
              </div>
              <div>
                {{
                  task.data().dateScheduled !== null
                    ? $utils.momentFromTimestamp(task.data().dateScheduled).format('ddd Do MMM YYYY')
                    : 'Unscheduled'
                }}
                <v-icon v-if="task.data().complete" size="small">mdi-check</v-icon>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="d-flex justify-end mx-2 pb-4">
          <v-btn color="accent" variant="flat" @click="updateTasks(selectedShipment)">Update Tasks</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notes dialog -->
    <v-dialog
      v-model="editNotesDialog"
      width="500"
      @update:model-value="
        () => {
          selectedShipment = {}
          selectedQuote = null
          selectedVersionIndex = null
        }
      "
    >
      <v-card>
        <v-card-text>
          <v-progress-linear :indeterminate="true" v-if="selectedVersionIndex == null"></v-progress-linear>
          <v-textarea
            v-else
            variant="outlined"
            label="Order notes"
            hide-details
            v-model="selectedQuote.versions[selectedVersionIndex].notes"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="editNotesDialog = false">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="updateNotes" :disabled="notesSubmitting">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QuoteEdit from '@/views/QuoteEdit'
import MaterialOrderView from '@/components/MaterialOrderView'
import DatePickerPlus from '@/components/tables/DatePickerPlus'
import mu from '@/js/mu'
import { auth, db, functions } from '@/js/db'
import { arrayUnion, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'Shipments',
  components: { MaterialOrderView, QuoteEdit, DatePickerPlus },
  data() {
    return {
      tallyOrders: [],
      shipments: [],
      loading: true,
      selectedShipment: {},
      selectedOrder: null,
      overlayComponent: null,
      dialogTitle: null,
      dialogVisible: false,
      scheduleDialog: false,
      taskSchedule: null,
      pauseShipmentDialog: false,
      deleteShipmentDialog: false,
      dispatchedDialog: false,
      dispatching: false,
      dispatchError: null,
      dispatchedInvoiceURL: null,
      dispatchCompleted: false,
      editNotesDialog: false,
      selectedQuote: null,
      selectedVersionIndex: null,
      notesSubmitting: false,
      toolpathMode: false,
      sortBy: [{ key: 'dateScheduled', order: 'asc' }],
      toolpathSortBy: [{ key: 'deliveryDate', order: 'asc' }],
      pauseForm: {
        why: null,
        changesCheck: null,
        changes: null
      },
      editPauseLogDialog: false,
      deletePauseLogDialog: false,
      deletingPauseLog: false,
      selectedPauseLog: {},
      selectedPauseLogKey: null,
      toolpathModeHeaders: [
        {
          title: 'Order #',
          sortable: false,
          width: 100,
          key: 'orderNumber'
        },
        {
          title: 'Customer',
          sortable: false,
          key: 'customerEmail'
        },
        {
          title: 'Items',
          sortable: false,
          key: 'lineItems'
        },
        {
          title: 'Material Due In',
          sortable: true,
          key: 'materialOrdered'
        },
        {
          title: 'Toolpathed',
          sortable: true,
          key: 'toolpathed'
        },
        {
          title: 'Elapsed Day from Payment',
          sortable: true,
          key: 'paymentDate'
        },
        {
          title: 'Deadline Till Delivery',
          sortable: true,
          key: 'deliveryDate'
        },
        {
          title: 'Scheduled Delivery',
          sortable: true,
          key: 'dateScheduled'
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'center',
          width: 100,
          key: null
        }
      ],
      headers: [
        {
          title: 'Order #',
          sortable: false,
          width: 100,
          key: 'orderNumber'
        },
        {
          title: 'Customer',
          sortable: false,
          key: 'customerEmail'
        },
        {
          title: 'Items',
          sortable: false,
          key: 'lineItems'
        },
        {
          title: 'Material Due In',
          sortable: true,
          key: 'materialOrdered'
        },
        {
          title: 'Toolpathed',
          sortable: true,
          key: 'toolpathed'
        },
        {
          title: 'Payment Date',
          sortable: true,
          key: 'paymentDate'
        },
        {
          title: 'Sprayer Date',
          sortable: true,
          key: 'sprayerDate'
        },
        {
          title: 'Target Delivery',
          sortable: true,
          key: 'deliveryDate'
        },
        {
          title: 'Scheduled Delivery',
          sortable: true,
          key: 'dateScheduled'
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'end',
          width: 230,
          key: null
        }
      ],
      tableSearch: ''
    }
  },
  methods: {
    calcDate(type, date) {
      let now = new Date()
      let then = new Date(date.seconds * 1000)
      let diff
      if (type == 'deadline') {
        diff = Math.floor((then - now) / (1000 * 60 * 60 * 24))
        return diff
      } else if (type == 'payment') {
        diff = Math.floor((now - then) / (1000 * 60 * 60 * 24))
        return diff
      }
    },
    changed(shipment, attribute) {
      // update in firebase
      updateDoc(doc(db, 'shipments', shipment.id), {
        [attribute]: shipment[attribute]
      })
        .then(() => {
          this.$store.commit('openSnackbar', {
            snackbarText: 'Shipment Updated 🙌'
          })
        })
        .catch((err) => {
          console.error(err)
        })
    },
    customSearch(value, search, item) {
      // V3 hands the internal item ({ raw, columns, key, value }), not the row — .raw is V2's `item`.
      return Object.values(item.raw).some((v) => v && v.toString().toLowerCase().includes(search))
    },
    scheduleDialogClose() {
      this.taskSchedule = null
    },
    formatLineItems(a) {
      var s = ''
      for (var i = 0; i < a.length; i++) {
        s += a[i]
        if (i < a.length - 1) s += ', '
      }
      return s
    },
    onSortByChange(sortBy) {
      if (this.toolpathMode) this.toolpathSortBy = sortBy
      else this.sortBy = sortBy
    },
    viewQuote(shipment) {
      this.dialogVisible = true
      this.selectedShipment = shipment
      this.dialogTitle = shipment.customerEmail
      this.overlayComponent = 'QuoteEdit'
    },
    materialView(shipment) {
      this.dialogVisible = true
      this.selectedShipment = shipment
      this.dialogTitle = 'Material Order List'
      this.overlayComponent = 'MaterialOrderView'
    },
    sprayerView(shipment) {
      var routeData = this.$router.resolve({
        name: 'sprayerView',
        params: { shipmentID: shipment.id }
      })
      window.open(routeData.href, '_blank')
    },
    packingView(shipment) {
      var routeData = this.$router.resolve({
        name: 'packingView',
        params: { shipmentID: shipment.id }
      })
      window.open(routeData.href, '_blank')
    },
    editNotes(shipment) {
      this.selectedShipment = shipment
      this.editNotesDialog = true

      getDoc(doc(db, 'quotes', shipment.quoteID)).then((quoteRef) => {
        this.selectedQuote = quoteRef.data()

        for (var i = 0; i < this.selectedQuote.versions.length; i++) {
          if (this.selectedQuote.versions[i].id == shipment.versionID) {
            this.selectedVersionIndex = i
          }
        }
      })
    },
    updateNotes() {
      this.editNotesDialog = false
      this.notesSubmitting = true

      updateDoc(doc(db, 'quotes', this.selectedShipment.quoteID), {
        versions: this.selectedQuote.versions
      }).then(() => {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Notes Updated 🙌'
        })
        this.notesSubmitting = false
        this.selectedQuote = null
        this.selectedShipment = {}
        this.selectedVersionIndex = null
      })
    },
    schedule(shipment) {
      this.selectedShipment = shipment
      this.scheduleDialog = true
      getDocs(query(collection(db, 'tasks'), where('shipmentID', '==', shipment.id))).then((tasks) => {
        this.taskSchedule = tasks.docs.sort((a, b) => {
          return a.data().dateScheduled > b.data().dateScheduled ? 1 : -1
        })
      })
    },
    async toggleShipmentPause(shipment) {
      let pauseLog
      let updateObj = {
        paused: !this.selectedShipment.paused
      }
      if (!this.selectedShipment.paused) {
        // paused state
        if (!(await this.$utils.validateForm(this.$refs.pauseForm))) {
          return
        } else {
          if (!this.selectedShipment.pauseLog) {
            pauseLog = [
              {
                why: this.pauseForm.why,
                changes: this.pauseForm.changes ? this.pauseForm.changes : 'None',
                date: this.$utils.tsNow(),
                author: auth.currentUser.email
              }
            ]
          } else {
            pauseLog = arrayUnion({
              why: this.pauseForm.why,
              changes: this.pauseForm.changes ? this.pauseForm.changes : 'None',
              date: this.$utils.tsNow(),
              author: auth.currentUser.email
            })
          }

          // save change statement (will always be latest pause log) in selectedQuote
          this.selectedQuote.versions[this.selectedVersionIndex].changeStatement = this.pauseForm.changes
            ? this.pauseForm.changes
            : ''

          // then update to quotes db
          updateDoc(doc(db, 'quotes', this.selectedShipment.quoteID), {
            versions: this.selectedQuote.versions
          }).then(() => {
            this.selectedQuote = null
            this.selectedVersionIndex = null
          })
        }
        updateObj.pauseLog = pauseLog
      }

      this.pauseShipmentDialog = false

      this.pauseForm.why = null
      this.pauseForm.changes = null
      if (this.pauseForm.changesCheck) this.pauseForm.changesCheck = null

      updateDoc(doc(db, 'shipments', shipment.id), updateObj)
        .then(() => {
          this.$store.commit('openSnackbar', { snackbarText: 'Shipment Updated' })
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })
    },
    findQuoteFromShipment() {
      getDoc(doc(db, 'quotes', this.selectedShipment.quoteID)).then((quoteRef) => {
        this.selectedQuote = quoteRef.data()

        for (var i = 0; i < this.selectedQuote.versions.length; i++) {
          if (this.selectedQuote.versions[i].id == this.selectedShipment.versionID) {
            this.selectedVersionIndex = i
          }
        }
      })
    },
    updatePauseLog(key) {
      let updateObj = {
        paused: this.selectedShipment.paused
      }

      updateObj.pauseLog = this.selectedShipment.pauseLog

      this.editPauseLogDialog = false
      this.selectedPauseLog = {}

      updateDoc(doc(db, 'shipments', this.selectedShipment.id), updateObj)
        .then(() => {
          this.$store.commit('openSnackbar', { snackbarText: 'Pause Log Updated' })
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })

      // Trying to find quote with same quote ID from selected shipment
      if (key && key == this.selectedShipment.pauseLog.length - 1) {
        // add latest pausedLog to selectedQuote as changeStatement
        this.selectedQuote.versions[this.selectedVersionIndex].changeStatement =
          this.selectedShipment.pauseLog[key].changes

        // then update to quotes db to generate prod notes
        updateDoc(doc(db, 'quotes', this.selectedShipment.quoteID), {
          versions: this.selectedQuote.versions
        }).then(() => {
          this.$store.commit('openSnackbar', {
            snackbarText: '🗒️ Changes added as Prod Notes!'
          })
          this.selectedQuote = null
          this.selectedVersionIndex = null
        })
      }
    },
    deletePauseLog(key) {
      this.deletePauseLogDialog = false
      this.deletingPauseLog = true

      let generateProdNote
      if (key == this.selectedShipment.pauseLog.length - 1 && key != 0) generateProdNote = true

      let clearChangeStatement
      if (key == 0) clearChangeStatement = true

      // then will delete that log!
      this.selectedShipment.pauseLog.splice(key, 1)

      if (generateProdNote) {
        let latest = this.selectedShipment.pauseLog.length - 1
        // add latest pausedLog to selectedQuote as changeStatement
        this.selectedQuote.versions[this.selectedVersionIndex].changeStatement =
          this.selectedShipment.pauseLog[latest].changes
      } else if (clearChangeStatement) {
        this.selectedQuote.versions[this.selectedVersionIndex].changeStatement = ''
      }

      // then update to quotes db to generate prod notes
      updateDoc(doc(db, 'quotes', this.selectedShipment.quoteID), {
        versions: this.selectedQuote.versions
      }).then(() => {
        this.$store.commit('openSnackbar', {
          snackbarText: '🗒️ Changes added as Prod Notes!'
        })
      })

      let updateObj = {
        paused: this.selectedShipment.paused
      }
      updateObj.pauseLog = this.selectedShipment.pauseLog
      updateDoc(doc(db, 'shipments', this.selectedShipment.id), updateObj)
        .then(() => {
          this.$store.commit('openSnackbar', { snackbarText: 'Pause Log Updated' })
          this.deletingPauseLog = false
          this.selectedQuote = null
          this.selectedVersionIndex = null
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })
    },
    deleteShipment(shipment) {
      this.deleteShipmentDialog = false
      deleteDoc(doc(db, 'shipments', shipment.id))
        .then(() => {
          this.$store.commit('openSnackbar', { snackbarText: 'Order Deleted' })
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })
    },
    async openDispatchDialog(shipment) {
      this.selectedShipment = shipment
      this.selectedOrder = null
      this.dispatchError = null
      this.dispatchedInvoiceURL = null
      this.dispatching = false
      this.dispatchCompleted = false
      this.dispatchedDialog = true
      try {
        const orderSnap = await getDoc(doc(db, 'orders', shipment.orderID))
        this.selectedOrder = { id: orderSnap.id, ...orderSnap.data() }
      } catch (e) {
        this.dispatchError = 'Could not load order: ' + (e?.message || e)
      }
    },
    async confirmDispatch() {
      this.dispatching = true
      this.dispatchError = null
      try {
        const dispatchShipmentFn = httpsCallable(functions, 'dispatchShipment')
        const result = await dispatchShipmentFn({ shipmentId: this.selectedShipment.id })
        this.dispatchedInvoiceURL = result?.data?.xeroInvoiceURL || null
        // refresh selectedOrder to reflect the counter increment
        const orderSnap = await getDoc(doc(db, 'orders', this.selectedShipment.orderID))
        this.selectedOrder = { id: orderSnap.id, ...orderSnap.data() }
        this.dispatchCompleted = true
        this.$store.commit('openSnackbar', {
          snackbarText: this.dispatchedInvoiceURL ? 'Dispatched and invoice raised' : 'Shipment dispatched'
        })
        if (this.selectedOrder && this.selectedOrder.region !== 'eu') {
          this.dispatchedDialog = false
        }
      } catch (e) {
        this.dispatchError = e?.message || String(e)
      } finally {
        this.dispatching = false
      }
    },
    async resetDispatchCounter() {
      if (!this.selectedOrder) return
      this.dispatching = true
      this.dispatchError = null
      try {
        const resetFn = httpsCallable(functions, 'resetDispatchCounter')
        await resetFn({ orderId: this.selectedOrder.id })
        const orderSnap = await getDoc(doc(db, 'orders', this.selectedOrder.id))
        this.selectedOrder = { id: orderSnap.id, ...orderSnap.data() }
        this.$store.commit('openSnackbar', { snackbarText: 'Dispatch counter reset' })
      } catch (e) {
        this.dispatchError = e?.message || String(e)
      } finally {
        this.dispatching = false
      }
    },
    async updateTasks(shipment) {
      const quote = await getDoc(doc(db, 'quotes', shipment.quoteID))

      let quoteData = quote.data()

      const version = quoteData.versions.find((v) => v.id === shipment.versionID)
      version.mu = mu.get(version)

      // update quote with quoteData object
      await updateDoc(doc(db, 'quotes', shipment.quoteID), quoteData)

      // update tasks
      const updateTasksForOrderFunc = httpsCallable(functions, 'updateTasksForOrder')
      const result = await updateTasksForOrderFunc(shipment.orderID)
      this.scheduleDialog = false

      const skipped = result?.data?.skippedShipped || []
      if (skipped.length) {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Tasks not updated — this shipment has already shipped',
          timeout: 5000
        })
      } else {
        this.$store.commit('openSnackbar', { snackbarText: 'Tasks updated' })
      }
    },
    printableSVG(shipment) {
      var routeData = this.$router.resolve({
        name: 'customerSVG',
        params: {
          quoteID: shipment.quoteID,
          versionID: shipment.versionID
        }
      })
      window.open(routeData.href, '_blank')
    }
  },
  async created() {
    await this.$live(
      'tallyOrders',
      query(collection(db, 'orders'), where('foc', '==', false), where('dispatched', '==', false))
      // where('numPayments', '>', 0)
    )
    await this.$live(
      'shipments',
      query(collection(db, 'shipments'), where('shipped', '==', false), where('readyForDispatch', '==', false))
    )

    this.loading = false
  },
  computed: {
    pauseBlocked() {
      if (!this.selectedShipment || this.selectedShipment.paused) return false
      if (!this.pauseForm.changesCheck) return false
      return !(this.pauseForm.changes && this.pauseForm.changes.trim())
    },
    isAdmin() {
      return !!this.$store.state.user.permissions.admin
    },
    activeSortBy() {
      return this.toolpathMode ? this.toolpathSortBy : this.sortBy
    },
    shipmentsForToolpath() {
      return this.shipments.filter((shipment) => shipment.toolpathed == null || shipment.materialOrdered == null)
    },
    ukOrderTally() {
      let totalSum = 0
      for (const order of this.tallyOrders) {
        totalSum += order?.total
      }
      return 'Orders Totals = £' + this.$numeral(totalSum).format('0,0.00')
    }
  }
}
</script>
<style lang="scss" scoped>
#production-table {
  width: 100%;

  #tasks-dialog {
    .task {
      margin: 10px 0;
      padding: 10px 10px;
      border-radius: 5px;
      display: flex;
      justify-content: space-between;
      background: #eee;
      .details {
        text-transform: capitalize;
      }
    }
  }
  .text-truncate {
    max-width: 150px;
  }
}
.pause-log-item {
  padding: 8px;
  border: solid 1px grey;
}
</style>
